import type { User } from '../api/types';
import type { ApiResponse } from '../api/types';

/**
 * Extracts and validates user data from API response
 * Handles both nested (response.data.user) and direct (response.data) structures
 *
 * @param response - API response that may contain user data
 * @returns Validated User object or null if invalid/missing
 */
export const extractAndValidateUser = (
  response: ApiResponse<unknown>,
): User | null => {
  // Check if response is successful and has data
  if (!response.success || !response.data) {
    return null;
  }

  // Extract user from nested structure (response.data.user) or use direct (response.data)
  // Type assertion needed because response.data is unknown
  const data = response.data as { user?: User } | User;
  const userData = 'user' in data && data.user ? data.user : (data as User);

  // Validate that user data has required fields (email and role)
  // Check if it's a valid User object with required properties
  if (
    userData &&
    typeof userData === 'object' &&
    'email' in userData &&
    'role' in userData
  ) {
    return userData as User;
  }

  // Return null if user data is missing required fields
  return null;
};
